/**
 * Sentinel Response™ - Client Portal Layout
 * Branded layout for client organization users.
 */
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Ticket, Server, LogOut, ChevronDown,
  Plus, HelpCircle, Activity,
} from 'lucide-react';
import { useState, useMemo } from 'react';

const navItems = [
  { to: '/portal/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/portal/tickets', icon: Ticket, label: 'Tickets' },
  { to: '/portal/assets', icon: Server, label: 'Assets' },
  { to: '/portal/monitoring', icon: Activity, label: 'Monitoring' },
];

export default function ClientLayout() {
  const { user, tenant, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const brandPrimary = tenant?.primary_color || '#1A3A5C';

  return (
    <div className="min-h-screen flex bg-gray-950">
      <aside className="w-64 bg-gray-900/95 border-r border-gray-800 flex flex-col">
        {/* Client branding header */}
        <div className="p-5 border-b border-gray-800">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.name} className="h-12 w-auto object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-lg"
                style={{ backgroundColor: brandPrimary }}
              >
                {tenant?.name?.[0] || 'C'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{tenant?.name || 'Client Portal'}</div>
                <div className="text-[11px] text-gray-500">Technology Portal</div>
              </div>
            </div>
          )}
        </div>

        {/* New ticket button */}
        <div className="p-3">
          <NavLink
            to="/portal/tickets/new"
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:shadow-lg hover:opacity-90 active:scale-[0.98]"
            style={{ backgroundColor: brandPrimary }}
          >
            <Plus className="w-4 h-4" />
            Submit a Ticket
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-1 space-y-0.5">
          <div className="text-[10px] font-medium text-gray-600 uppercase tracking-widest px-3 pt-2 pb-2">Navigation</div>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? 'text-white font-medium shadow-sm'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
                }`
              }
              style={({ isActive }) =>
                isActive ? { backgroundColor: `${brandPrimary}20`, borderLeft: `3px solid ${brandPrimary}` } : {}
              }
            >
              <Icon className="w-[18px] h-[18px]" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Support */}
        <div className="px-3 pb-2">
          <div className="text-[10px] font-medium text-gray-600 uppercase tracking-widest px-3 pt-2 pb-2">Support</div>
          <a href="mailto:support@sentinelgov.com"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 transition-colors">
            <HelpCircle className="w-[18px] h-[18px]" />
            Contact Support
          </a>
        </div>

        {/* Powered by */}
        <div className="px-5 py-3 border-t border-gray-800">
          <div className="flex items-center gap-2">
            <img src="/logo-sentinel-icon.svg" alt="" className="w-4 h-4 opacity-50" />
            <span className="text-[10px] text-gray-600 tracking-wider uppercase">
              Powered by Sentinel Response
            </span>
          </div>
        </div>

        {/* User */}
        <div className="p-3 border-t border-gray-800">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-800/60 transition-colors"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white"
                style={{ backgroundColor: brandPrimary }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-gray-200 text-sm truncate">{user?.first_name} {user?.last_name}</div>
                <div className="text-xs text-gray-500 truncate">{user?.job_title || user?.email}</div>
              </div>
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-20">
                  <div className="px-3 py-2 border-b border-gray-700">
                    <div className="text-xs text-gray-400">{user?.email}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5 capitalize">{user?.role?.replace('_', ' ')}</div>
                  </div>
                  <button onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-gray-700 transition-colors">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
